import { useCallback, useEffect, useRef, useState } from "react";
import { listDependents, listEmployers } from "@/features/clients/api";
import type { Dependent, Employer } from "@/features/clients/types";
import { ApiError } from "@/lib/api";

interface UseClientRecordsResult<T> {
  records: T[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Fetches a client's sub-record collection (dependents/employers) whenever the
 * client id changes. Same guards as `use-clients.ts`: `lastKeyRef` collapses
 * StrictMode's dev double-invoke, `requestIdRef` drops out-of-order responses.
 */
function useClientRecords<T>(
  clientId: string,
  fetcher: (clientId: string) => Promise<T[]>,
  errorFallback: string,
): UseClientRecordsResult<T> {
  const [records, setRecords] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const lastKeyRef = useRef<string | null>(null);
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);

  const refetch = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const key = `${reloadKey}|${clientId}`;
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;

    const reqId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    fetcher(clientId)
      .then((result) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setRecords(result);
      })
      .catch((err) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setRecords([]);
        setError(err instanceof ApiError ? err.message : errorFallback);
      })
      .finally(() => {
        if (mountedRef.current && requestIdRef.current === reqId) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetcher/fallback are static per call site
  }, [clientId, reloadKey]);

  return { records, loading, error, refetch };
}

export function useDependents(clientId: string): UseClientRecordsResult<Dependent> {
  return useClientRecords(clientId, listDependents, "Failed to load dependents.");
}

export function useEmployers(clientId: string): UseClientRecordsResult<Employer> {
  return useClientRecords(clientId, listEmployers, "Failed to load employers.");
}
